/**
 * ComponentRegistry.js — CRA/webpack equivalent of Vite's import.meta.glob
 *
 * Uses webpack's require.context with lazy mode to auto-discover all .jsx/.js
 * files under /src/pages and /src/components.
 *
 * Returns a map: componentPath → () => Promise<Module>
 * so React.lazy can load any registered component by its relative path string.
 *
 * ComponentPath format (as stored in ERPScreenSources.ComponentPath):
 *   "pages/Dashboard.jsx"
 *   "components/IndustryMasterManager.jsx"
 */

const _registry = {};

/* ── Auto-scan pages ──────────────────────────────────────────────────────── */
try {
  const pagesCtx = require.context('../pages', true, /\.(jsx|js)$/, 'lazy');
  pagesCtx.keys().forEach(k => {
    const rel = `pages/${k.replace(/^\.\//, '')}`;     // "./Dashboard.jsx" → "pages/Dashboard.jsx"
    _registry[rel] = () => pagesCtx(k);
  });
} catch (_) { /* no pages dir */ }

/* ── Auto-scan components ─────────────────────────────────────────────────── */
try {
  const compsCtx = require.context('../components', true, /\.(jsx|js)$/, 'lazy');
  compsCtx.keys().forEach(k => {
    const rel = `components/${k.replace(/^\.\//, '')}`;
    _registry[rel] = () => compsCtx(k);
  });
} catch (_) { /* no components dir */ }

/**
 * Resolve a ComponentPath string to a lazy-loadable module factory.
 * Returns null if the path is not registered (unrecognised external component).
 *
 * @param {string} componentPath  e.g. "pages/Dashboard.jsx"
 * @returns {(() => Promise<Module>) | null}
 */
export function resolveComponent(componentPath) {
  if (!componentPath) return null;
  // Normalise: strip leading ./ or /src/
  const clean = componentPath.replace(/^(\.\/|\/src\/|src\/)/, '');
  return _registry[clean] || null;
}

/**
 * All component paths registered in this build.
 * Useful for debugging and manifest generation.
 */
export function getRegistryPaths() {
  return Object.keys(_registry);
}
